import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { DragHandle, useReorderSensors } from '@/components/ui/reorder';
import { EmptyState } from '@/components/ui/states';
import { localizedName } from '@/lib/localized';
import { cn } from '@/lib/utils';
import type { CatalogItem, Category } from '../lib';
import { CheckRow, FilterChip } from './controls';
import { type DraftItem, reorderItems, selectionOfShown, toggleAllShown } from './wizardState';

/**
 * Step 3 — pick products on the left, order them on the right.
 *
 * Two things the frame does not draw and this keeps, because both change what
 * the operator ships:
 *
 * - **The sold-out badge.** `guestMenu` filters `isSoldOut: false`
 *   (`menus.service.ts:169`), so a sold-out product attached here reaches no
 *   guest until stock returns. Attaching it is legitimate; not saying so is not.
 * - **Both empty states.** The right panel opens empty *by definition* — it is
 *   the first thing anyone reaching this step sees — and the left one is empty
 *   for a tenant with no catalog, and for a search that matched nothing.
 *
 * Select all acts on **what is shown**, never on the catalogue. A tenant here
 * has hundreds of items, and a control that swept up everything behind an active
 * search or category chip would attach rows the operator never laid eyes on. The
 * count is in the label for exactly that reason: it is the number the operator
 * checks against what is on screen before committing. It is a real tri-state
 * `<input type="checkbox">` rather than the shared `Switch`, because half the
 * shown rows being selected is a third state and a switch has two.
 *
 * Prices are `useCurrency(null)`, the tenant base currency, because `basePrice`
 * is a tenant-level price and not a branch override. That call is inherited
 * from the tab and deliberate; a branch-scoped menu does not change it.
 */
export function ItemPicker({
  items,
  categories,
  selected,
  onChange,
  formatMoney,
  readOnly,
}: {
  items: CatalogItem[];
  categories: Category[];
  selected: DraftItem[];
  onChange: (next: DraftItem[]) => void;
  formatMoney: (amount: string) => string;
  /** No `menu.update`: the products are visible, the selection is not offered. */
  readOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const sensors = useReorderSensors();

  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const chosen = useMemo(() => new Set(selected.map((row) => row.itemId)), [selected]);

  /**
   * Filtering is client-side because the whole catalog is already in hand —
   * `Catalog.tsx` loads `limit: 200` once and hands it down. Round-tripping
   * `?search=` for a list already in memory would put a network hop between a
   * keystroke and a filter.
   */
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      if (categoryId && item.categoryId !== categoryId) return false;
      if (!needle) return true;
      return (
        localizedName(item, i18n.language).toLowerCase().includes(needle) ||
        (item.sku ?? '').toLowerCase().includes(needle)
      );
    });
  }, [items, search, categoryId, i18n.language]);

  const shownIds = useMemo(() => shown.map((item) => item.id), [shown]);

  const toggle = (item: CatalogItem, on: boolean) =>
    onChange(
      on
        ? [...selected, { itemId: item.id, isVisible: true }]
        : selected.filter((row) => row.itemId !== item.id),
    );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    onChange(reorderItems(selected, String(active.id), String(over.id)));
  };

  const nameOf = (itemId: string) => {
    const item = byId.get(itemId);
    return item ? localizedName(item, i18n.language) : itemId;
  };

  const announce = (key: string, id: string | number) => {
    const index = selected.findIndex((row) => row.itemId === String(id));
    if (index < 0) return undefined;
    return t(key, { name: nameOf(String(id)), index: index + 1, count: selected.length });
  };

  const accessibility = {
    screenReaderInstructions: { draggable: t('catalog.reorder.instructions') },
    announcements: {
      onDragStart: ({ active }: { active: { id: string | number } }) =>
        announce('catalog.reorder.picked', active.id),
      onDragOver: ({ over }: { over: { id: string | number } | null }) =>
        over ? announce('catalog.reorder.movedOver', over.id) : undefined,
      onDragEnd: ({ active }: { active: { id: string | number } }) =>
        announce('catalog.reorder.dropped', active.id),
      onDragCancel: ({ active }: { active: { id: string | number } }) =>
        announce('catalog.reorder.cancelled', active.id),
    },
  };

  // `min-w-0` on both panels for the same reason foundation §7.5 puts it on
  // inputs: a grid item's `min-width` is `auto`, so the card floors at the
  // intrinsic width of its widest child — here the horizontally-scrolling chip
  // row — and pushes the whole page sideways at 320.
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>{t('catalog.tabs.items')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('catalog.items.searchPlaceholder')}
            aria-label={t('common.search')}
          />

          {categories.length > 0 && (
            <div className="scrollbar-thin flex gap-1.5 overflow-x-auto pb-1">
              <FilterChip active={!categoryId} onClick={() => setCategoryId('')}>
                {t('catalog.items.allCategories')}
              </FilterChip>
              {categories.map((category) => (
                <FilterChip
                  key={category.id}
                  active={categoryId === category.id}
                  onClick={() => setCategoryId(category.id)}
                >
                  {localizedName(category, i18n.language)}
                </FilterChip>
              ))}
            </div>
          )}

          {shown.length > 0 && (
            <SelectAllShown
              state={selectionOfShown(shownIds, selected)}
              count={shown.length}
              disabled={readOnly}
              onChange={(on) => onChange(toggleAllShown(selected, shownIds, on))}
            />
          )}

          {shown.length === 0 ? (
            <EmptyState
              title={t('catalog.items.empty')}
              hint={search || categoryId ? undefined : t('catalog.items.emptyHint')}
            />
          ) : (
            <ul className="max-h-96 space-y-1 overflow-y-auto">
              {shown.map((item) => {
                const on = chosen.has(item.id);
                return (
                  <li key={item.id}>
                    <CheckRow
                      checked={on}
                      disabled={readOnly}
                      onChange={(next) => toggle(item, next)}
                      className={on ? undefined : 'text-ink-muted'}
                    >
                      <span dir="auto" className="min-w-0 flex-1 truncate text-sm">
                        {localizedName(item, i18n.language)}
                      </span>
                      {item.isSoldOut && (
                        <Badge tone="danger" className="shrink-0">
                          {t('catalog.items.soldOut')}
                        </Badge>
                      )}
                      <span className="shrink-0 text-sm tabular-nums">
                        {formatMoney(item.basePrice)}
                      </span>
                    </CheckRow>
                  </li>
                );
              })}
            </ul>
          )}

          {/* The live region for the whole panel. Select all changes many rows
              at once, so the resulting count has to be spoken, not only drawn. */}
          <p role="status" aria-live="polite">
            <Badge tone="brand">{t('catalog.items.bulk.selected', { count: selected.length })}</Badge>
          </p>
        </CardContent>
      </Card>

      <Card className="min-w-0">
        <CardHeader className="flex-row items-baseline justify-between gap-2">
          <CardTitle>{t('catalog.menus.items')}</CardTitle>
          <span className="shrink-0 text-xs text-ink-muted">{t('catalog.items.sortOrder')}</span>
        </CardHeader>
        <CardContent className="space-y-3">
          {selected.length === 0 ? (
            <EmptyState title={t('catalog.menus.noItems')} hint={t('catalog.menus.noItemsHint')} />
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                accessibility={accessibility}
                onDragEnd={onDragEnd}
              >
                <ul className="space-y-1">
                  <SortableContext
                    items={selected.map((row) => row.itemId)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selected.map((row) => (
                      <SelectedRow
                        key={row.itemId}
                        row={row}
                        name={nameOf(row.itemId)}
                        price={formatMoney(byId.get(row.itemId)?.basePrice ?? '0')}
                        readOnly={readOnly}
                        onToggleVisible={() =>
                          onChange(
                            selected.map((entry) =>
                              entry.itemId === row.itemId
                                ? { ...entry, isVisible: !entry.isVisible }
                                : entry,
                            ),
                          )
                        }
                        onDetach={() =>
                          onChange(selected.filter((entry) => entry.itemId !== row.itemId))
                        }
                      />
                    ))}
                  </SortableContext>
                </ul>
              </DndContext>
              <p className="text-xs text-ink-muted">
                {t('catalog.menus.wizard.reorderHint')}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SelectedRow({
  row,
  name,
  price,
  readOnly,
  onToggleVisible,
  onDetach,
}: {
  row: DraftItem;
  name: string;
  price: string;
  readOnly?: boolean;
  onToggleVisible: () => void;
  onDetach: () => void;
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.itemId, disabled: readOnly });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-2 py-1',
        isDragging && 'opacity-50',
        !row.isVisible && 'text-ink-muted',
      )}
    >
      <DragHandle
        ref={setActivatorNodeRef}
        label={t('catalog.items.reorder.handle', { name })}
        dragging={isDragging}
        disabled={readOnly}
        className="h-9"
        {...attributes}
        {...listeners}
      />
      <span dir="auto" className="min-w-0 flex-1 truncate text-sm">
        {name}
      </span>
      <span className="shrink-0 text-sm tabular-nums">{price}</span>
      <button
        type="button"
        disabled={readOnly}
        onClick={onToggleVisible}
        className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        {/* `isVisible` is not a `DomainStatus`, so the tone is passed directly
            rather than through `toneForStatus`. */}
        <Badge tone={row.isVisible ? 'success' : 'neutral'}>
          {t(row.isVisible ? 'catalog.menus.visible' : 'catalog.menus.hidden')}
        </Badge>
      </button>
      <Button
        size="sm"
        variant="ghost"
        className="h-9 w-9 shrink-0 p-0"
        disabled={readOnly}
        onClick={onDetach}
        aria-label={t('catalog.items.detach')}
        title={t('catalog.items.detach')}
      >
        <Icon as={X} aria-hidden />
      </Button>
    </li>
  );
}

/**
 * Select / deselect every shown row.
 *
 * `indeterminate` is a DOM property and not an attribute, so it has to be set
 * through a ref — and it is the whole point of the control: a box that reads
 * "checked" while half the visible rows are selected is telling the operator
 * something untrue about what the next click will do.
 *
 * The label carries the count and flips to `Deselect` once everything shown is
 * in, so the button always names the action it is about to take rather than the
 * state it is in.
 */
function SelectAllShown({
  state,
  count,
  disabled,
  onChange,
}: {
  state: 'none' | 'some' | 'all';
  count: number;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (box.current) box.current.indeterminate = state === 'some';
  }, [state]);

  return (
    <label
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-hover',
      )}
    >
      <input
        ref={box}
        type="checkbox"
        checked={state === 'all'}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 shrink-0 accent-[var(--accent-solid)]"
      />
      <span className="min-w-0">
        {state === 'all'
          ? t('catalog.menus.wizard.deselectAllShown', { count })
          : t('catalog.menus.wizard.selectAllShown', { count })}
      </span>
    </label>
  );
}
